<script lang="ts" src="./component.ts"></script>
<style lang="scss" src="./component.scss"></style>

<template>
  <SharedCard :class="b()">
    <div :class="b('header')">
      <SharedBack @click="handleBack" />

      <div :class="b('header-content')">
        <h3 :class="b('header-title')">
          {{ $t("Основная информация об организации") }}
        </h3>
        <p :class="b('header-label')">
          {{
            $t(
              "Ответьте на следующие вопросы, чтобы получить список необходимых документов"
            )
          }}
        </p>
      </div>
    </div>

    <form :class="b('form')" @submit.prevent="handleContinue">
      <section :class="b('form-section')">
        <h4 :class="b('form-section-title')">{{ $t("Регистрация") }}</h4>

        <div :class="b('form-grid', { registration: true })">
          <SharedValidate
            :class="b('form-country')"
            field="country"
            :value="kybBasicInfo.country"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.country"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("В какой стране зарегистрирована ваша организация?")
                }}<sup>*</sup>
              </h5>
              <SharedCountry
                view="light"
                placeholder="Выберите страну"
                :is-readonly="isReadonly"
                :is-error="isValidate && !kybBasicInfoFields.country"
                v-model:value="kybBasicInfo.country"
              />
            </div>
          </SharedValidate>

          <SharedValidate
            :class="b('form-date')"
            field="registration_date"
            :value="kybBasicInfo.registration_date"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.registration_date"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Дата регистрации") }}<sup>*</sup>
              </h5>
              <SharedDate
                view="light"
                format="dd-MM-yyyy"
                :is-readonly="isReadonly"
                :is-error="isValidate && !kybBasicInfoFields.registration_date"
                v-model:date="kybBasicInfo.registration_date"
              />
            </div>
          </SharedValidate>

          <SharedValidate
            :class="b('form-wide')"
            field="organisation"
            :value="kybBasicInfo.organisation"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.organisation"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Как называется ваша организация?") }}<sup>*</sup>
              </h5>
              <UIInput
                view="light"
                placeholder="ООО «Пример»"
                :is-readonly="isReadonly"
                :is-error="isValidate && !kybBasicInfoFields.organisation"
                v-model="kybBasicInfo.organisation"
              />
              <p :class="b('form-hint')">
                {{
                  $t(
                    "Полное название как в уставе, включая организационно-правовую форму."
                  )
                }}
              </p>
            </div>
          </SharedValidate>

          <SharedValidate
            :class="b('form-wide')"
            field="regional_number"
            :value="kybBasicInfo.regional_number"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.regional_number"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Какой у вас регистрационный номер?") }}<sup>*</sup>
              </h5>
              <UIInput
                view="light"
                placeholder="1234567890123"
                :is-readonly="isReadonly"
                :is-error="isValidate && !kybBasicInfoFields.regional_number"
                v-model="kybBasicInfo.regional_number"
              />
              <p :class="b('form-hint')">{{ $t("Для РФ — ОГРН, 13 цифр.") }}</p>
            </div>
          </SharedValidate>
        </div>
      </section>

      <section :class="b('form-section')">
        <h4 :class="b('form-section-title')">{{ $t("Налоги и капитал") }}</h4>

        <div :class="b('form-grid')">
          <SharedValidate
            :class="b('form-wide')"
            field="inn"
            :value="kybBasicInfo.inn"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.inn"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Номер налогоплательщика") }}<sup>*</sup>
              </h5>
              <UIInput
                view="light"
                placeholder="7701234567"
                :is-readonly="isReadonly"
                :is-error="isValidate && !kybBasicInfoFields.inn"
                v-model="kybBasicInfo.inn"
              />
            </div>
          </SharedValidate>

          <SharedValidate
            :class="b('form-wide')"
            field="capital"
            :value="authorized_capital_amount"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.capital"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Уставный капитал") }}<sup>*</sup>
              </h5>
              <div :class="b('form-capital')">
                <UIInput
                  view="light"
                  type="number"
                  step="0.01"
                  placeholder="100 000"
                  :is-readonly="isReadonly"
                  :is-error="isValidate && !kybBasicInfoFields.capital"
                  v-model="authorized_capital_amount"
                />
                <UIInput
                  view="light"
                  placeholder="RUB"
                  :is-readonly="isReadonly"
                  :is-error="isValidate && !kybBasicInfoFields.capital"
                  v-model="authorized_capital_currency"
                />
              </div>
              <p :class="b('form-hint')">
                {{ $t("Введите сумму и валюту. Например: 100 000 RUB") }}
              </p>
            </div>
          </SharedValidate>
        </div>
      </section>

      <section :class="b('form-section')">
        <h4 :class="b('form-section-title')">
          {{ $t("Деятельность и контакты") }}
        </h4>

        <div :class="b('form-grid')">
          <SharedValidate
            :class="b('form-wide')"
            field="licensed"
            :value="kybBasicInfo.licensed"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.licensed"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Ваша деятельность лицензируется?") }}<sup>*</sup>
              </h5>
              <div :class="b('form-radios')">
                <div
                  :class="b('form-radio', { active: kybBasicInfo.licensed })"
                  @click="handleLicensedChange(true)"
                >
                  <SharedRadio
                    view="light"
                    :checked-value="true"
                    :is-readonly="isReadonly"
                    v-model:value="kybBasicInfo.licensed"
                  />
                  <p :class="b('form-radio-label')">{{ $t("Да") }}</p>
                </div>
                <div
                  :class="b('form-radio', { active: !kybBasicInfo.licensed })"
                  @click="handleLicensedChange(false)"
                >
                  <SharedRadio
                    view="light"
                    :checked-value="false"
                    :is-readonly="isReadonly"
                    v-model:value="kybBasicInfo.licensed"
                  />
                  <p :class="b('form-radio-label')">{{ $t("Нет") }}</p>
                </div>
              </div>
            </div>
          </SharedValidate>

          <SharedValidate
            :class="b('form-wide')"
            field="telegram"
            :value="kybBasicInfo.telegram"
            :is-validate="isValidate"
            v-model:field-value="kybBasicInfoFields.telegram"
          >
            <div :class="b('form-group')">
              <h5 :class="b('form-title')">
                {{ $t("Телеграм для связи") }}<sup>*</sup>
              </h5>
              <UIInput
                view="light"
                placeholder="@username"
                :is-telegram="true"
                :is-readonly="isReadonly"
                :is-error="isValidate && !kybBasicInfoFields.telegram"
                v-model="kybBasicInfo.telegram"
              />
            </div>
          </SharedValidate>
        </div>
      </section>

      <UIButton
        :class="b('continue')"
        label="Продолжить"
        view="main"
        :disabled="isContinueDisabled"
        type="submit"
      />
    </form>
  </SharedCard>
</template>
